import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Search, Eye, EyeOff, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import ImageUploader from '../../components/ImageUploader';

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, post: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, post: null });
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    image: '',
    author: '',
    isPublished: false
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/admin/blog');
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      image: '',
      author: 'Miss Baleng',
      isPublished: false
    });
    setEditDialog({ open: true, post: null });
  };

  const openEditDialog = (post) => {
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image: post.image,
      author: post.author,
      isPublished: post.isPublished
    });
    setEditDialog({ open: true, post });
  };

  const handleSave = async () => {
    try {
      if (editDialog.post) {
        await api.patch(`/admin/blog/${editDialog.post.id}`, formData);
        toast.success('Article mis à jour');
      } else {
        await api.post('/admin/blog', formData);
        toast.success('Article créé');
      }
      setEditDialog({ open: false, post: null });
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (postId) => {
    try {
      await api.delete(`/admin/blog/${postId}`);
      toast.success('Article supprimé');
      setDeleteDialog({ open: false, post: null });
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublish = async (post) => {
    try {
      await api.patch(`/admin/blog/${post.id}`, { isPublished: !post.isPublished });
      toast.success(post.isPublished ? 'Article dépublié' : 'Article publié');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredPosts = posts.filter(post => 
    post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion du Blog</h1>
            <p className="text-sm md:text-base text-[#5D4037] mt-1">Créez et gérez vos articles</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
            <Plus className="mr-2" size={20} />
            Nouvel article
          </Button>
        </div>
      </div>

      {/* Search - Mobile Optimized */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
            <Input
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Posts List - Mobile Optimized */}
      <div className="space-y-4">
        {filteredPosts.map((post) => (
          <Card key={post.id}>
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4">
                {/* Image */}
                {post.image ? (
                  <img src={post.image} alt={post.title} className="w-full sm:w-24 h-32 sm:h-24 object-cover rounded-lg shrink-0" />
                ) : (
                  <div className="w-full sm:w-24 h-32 sm:h-24 bg-[#F5F0EB] rounded-lg flex items-center justify-center shrink-0">
                    <FileText size={32} className="text-[#D4C4B5]" />
                  </div>
                )}
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">{post.category}</Badge>
                    {post.isPublished ? (
                      <Badge className="bg-green-100 text-green-800 text-xs">Publié</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800 text-xs">Brouillon</Badge>
                    )}
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-[#2C1810] line-clamp-1">{post.title}</h3>
                  <p className="text-xs md:text-sm text-[#5D4037] line-clamp-2 mt-1">{post.excerpt}</p>
                  <p className="text-xs text-[#A67B5B] mt-1">Par {post.author}</p>
                </div>
                
                {/* Desktop Actions */}
                <div className="hidden sm:flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => togglePublish(post)}
                    className={post.isPublished ? 'text-green-600' : 'text-gray-400'}
                    title={post.isPublished ? 'Dépublier' : 'Publier'}
                  >
                    {post.isPublished ? <Eye size={18} /> : <EyeOff size={18} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(post)}
                    className="text-[#5D4037] hover:text-[#2C1810]"
                  >
                    <Edit2 size={18} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteDialog({ open: true, post })}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
                
                {/* Mobile Actions */}
                <div className="flex sm:hidden gap-2 w-full pt-3 border-t border-[#E8DDD4]">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => togglePublish(post)}
                    className={`flex-1 ${post.isPublished ? 'text-green-600 border-green-300' : 'text-gray-500'}`}
                  >
                    {post.isPublished ? <><Eye size={14} className="mr-1" /> Publié</> : <><EyeOff size={14} className="mr-1" /> Brouillon</>}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(post)}
                    className="flex-1"
                  >
                    <Edit2 size={14} className="mr-1" /> Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteDialog({ open: true, post })}
                    className="text-red-600 border-red-300"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPosts.length === 0 && (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-[#D4C4B5] mb-4" />
          <p className="text-[#5D4037]">Aucun article trouvé</p>
        </div>
      )}

      {/* Edit/Create Dialog - Mobile Optimized */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="w-[95vw] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editDialog.post ? 'Modifier l\'article' : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Titre de l'article"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Conseils capillaires"
                />
              </div>
              <div className="space-y-2">
                <Label>Auteur</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Nom de l'auteur"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Extrait (résumé)</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brève description de l'article..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contenu</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Contenu complet de l'article..."
                rows={8}
              />
            </div>
            
            <ImageUploader
              category="blog"
              currentImage={formData.image}
              onImageUploaded={(url) => setFormData({ ...formData, image: url })}
              label="Image de l'article"
            />

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isPublished}
                onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
              />
              <Label>Publier l'article</Label>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, post: null })} className="w-full sm:w-auto">
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
                {editDialog.post ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - Mobile Optimized */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.post?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
